import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IDLE_MS = 30 * 60 * 1000; // 30 minutes

/**
 * InactivityLock — wraps sensitive dashboards for admin + clerk users.
 * After 30 minutes with no keyboard / mouse / touch activity, a fullscreen
 * overlay locks the UI. Correct password unlocks and resumes the same
 * session (JWT stays alive).
 */
export default function InactivityLock({ user }) {
  const [locked, setLocked] = useState(false);
  const [password, setPassword] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const timerRef = useRef(null);

  const shouldLock = user && (user.role === 'admin' || user.role === 'clerk');

  const resetTimer = useCallback(() => {
    if (!shouldLock) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setLocked(true), IDLE_MS);
  }, [shouldLock]);

  useEffect(() => {
    if (!shouldLock) return () => {};
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];
    const handler = () => { if (!locked) resetTimer(); };
    events.forEach(ev => window.addEventListener(ev, handler, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(ev => window.removeEventListener(ev, handler));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [shouldLock, locked, resetTimer]);

  const unlock = async (e) => {
    if (e) e.preventDefault();
    if (!password) { setError('Enter your password'); return; }
    setChecking(true); setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/auth/verify-password`, { password }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLocked(false);
      setPassword('');
      resetTimer();
      toast.success('Welcome back');
    } catch (err) {
      setError(err.response?.data?.detail || 'Incorrect password');
    } finally {
      setChecking(false);
    }
  };

  if (!shouldLock || !locked) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-950/95 backdrop-blur flex items-center justify-center p-4"
      data-testid="inactivity-lock-overlay"
    >
      <div className="max-w-md w-full bg-slate-900 border border-slate-700 rounded-2xl p-8 text-center shadow-2xl">
        <div className="mx-auto h-16 w-16 rounded-full bg-rose-500/15 flex items-center justify-center mb-4">
          <Lock className="h-8 w-8 text-rose-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-1" data-testid="inactivity-lock-title">🔒 ADMIN CLERK LOCKED</h2>
        <p className="text-sm text-slate-400 mb-1">Session locked due to inactivity (30 min)</p>
        <p className="text-xs text-slate-500 mb-6">Signed in as <span className="text-slate-300">{user?.full_name || user?.email}</span></p>
        <form onSubmit={unlock} className="space-y-3">
          <Input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter PIN / password to continue"
            className="bg-slate-950 border-slate-700 text-white text-center h-12"
            data-testid="inactivity-lock-input"
          />
          {error && <p className="text-rose-400 text-sm" data-testid="inactivity-lock-error">{error}</p>}
          <Button
            type="submit"
            disabled={checking}
            className="w-full bg-blue-600 hover:bg-blue-700 h-11"
            data-testid="inactivity-lock-submit"
          >
            {checking ? 'Checking…' : 'Unlock'}
          </Button>
        </form>
        <button
          type="button"
          onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href = '/login'; }}
          className="mt-4 text-xs text-slate-500 hover:text-slate-300"
          data-testid="inactivity-lock-signout"
        >
          Sign out instead
        </button>
      </div>
    </div>
  );
}
